import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useBuildProjectsQuery } from '@/hooks/build/useBuildProjectsQuery';
import ItemsManagement from '@/components/items/ItemsManagement';
import { Button } from '@/components/ui/button';
import { logger } from '@/lib/logger';

interface BuildItemsViewProps {
  initialProject?: string;
  initialAnchor?: string;
}

/**
 * Build Items View - Wraps ItemsManagement with build project filtering
 * Displays items only from projects with statuses: intake, tender, build, defects, archive
 * Excludes: service (maintenance) and inactive (discontinued) projects
 */
const BuildItemsView = ({ initialProject, initialAnchor }: BuildItemsViewProps) => {
  const { 
    data: buildData, 
    isLoading, 
    error 
  } = useBuildProjectsQuery(
    {
      searchQuery: '',
      projectGroup: undefined,
      projectType: undefined,
      teamLead: undefined,
      company: undefined
    }, 
    1, 
    100 // Large page size to get all build projects in one request
  );

  const buildProjectIds = useMemo(() => {
    // Phase 1: Frontend Safety Net - Filter out inactive projects client-side
    const allProjects = buildData?.projects || [];
    const safeBuildProjects = allProjects.filter(p => p.status !== 'inactive');
    const ids = safeBuildProjects.map(p => p.id);
    
    const filteredCount = allProjects.length - safeBuildProjects.length;
    
    logger.debug('[BuildItemsView] Build project IDs (with safety filter):', { 
      totalProjects: allProjects.length,
      safeBuildProjects: safeBuildProjects.length,
      filteredOutInactive: filteredCount,
      ids 
    });
    
    if (filteredCount > 0) {
      logger.warn('[BuildItemsView] Filtered out inactive projects:', {
        count: filteredCount,
        inactiveProjects: allProjects.filter(p => p.status === 'inactive').map(p => ({ id: p.id, name: p.project_name }))
      });
    }
    
    return ids;
  }, [buildData]);

  if (isLoading && buildData == null) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading build items...</p>
        </div>
      </div>
    );
  }

  if (error) {
    logger.error('[BuildItemsView] Error loading build projects:', error);
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-destructive">Error loading build projects</p>
        </div>
      </div>
    );
  }

  if (!buildProjectIds.length) {
    return (
      <div className="flex flex-col items-center justify-center p-8 gap-4">
        <p className="text-muted-foreground">Không tìm thấy dự án build nào</p>
        <Button variant="outline" asChild>
          <Link to="/admin/projects">Xem danh sách dự án</Link>
        </Button>
      </div>
    );
  }

  // Pass restrictedProjectIds to ensure only build project items are shown
  return (
    <div className="build-items-view">
      <ItemsManagement 
        initialProject={initialProject}
        initialAnchor={initialAnchor}
        restrictedProjectIds={buildProjectIds}
        itemTypeFilter="build"
        hideSearchInput={true}
      />
    </div>
  );
};

export default BuildItemsView;
